import React, { useRef } from "react";
import {
    motion,
    useScroll,
    useVelocity,
    useSpring,
    useTransform,
    useMotionValue,
    useAnimationFrame,
} from "framer-motion";

const wrap = (min, max, v) => {
    const range = max - min;
    return ((((v - min) % range) + range) % range) + min;
};

/**
 * Scroll-velocity-driven marquee. Direction reverses based on scroll direction.
 * baseVelocity = pixels/sec at idle.
 */
export const VelocityRow = ({
    children,
    baseVelocity = 60,         // base px/sec
    direction = 1,             // 1 = left, -1 = right
    scrollMultiplier = 4,
    className = "",
    style = {},
    copies = 4,
    ...rest
}) => {
    const baseX = useMotionValue(0);
    const { scrollY } = useScroll();
    const scrollVelocity = useVelocity(scrollY);
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 });
    const velocityFactor = useTransform(
        smoothVelocity,
        [0, 1000],
        [0, scrollMultiplier],
        { clamp: false }
    );
    const directionFactor = useRef(direction);

    const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`);

    useAnimationFrame((t, delta) => {
        // Keep the marquee always moving in the configured direction — never reverse
        // based on scroll direction. Scroll speed (absolute) only accelerates the row,
        // it does not flip it.
        directionFactor.current = direction;
        const baseMove = directionFactor.current * baseVelocity * (delta / 1000);
        const vf = velocityFactor.get();
        const moveBy = baseMove * (1 + Math.abs(vf));
        // Convert px to percent (rough — we work in % to handle responsive width)
        baseX.set(baseX.get() + moveBy * 0.05);
    });

    return (
        <div
            className={className}
            style={{
                overflow: "hidden",
                width: "100%",
                ...style,
            }}
            {...rest}
        >
            <motion.div
                style={{
                    x,
                    display: "flex",
                    width: "max-content",
                    flexWrap: "nowrap",
                    alignItems: "center",
                }}
            >
                {Array.from({ length: copies }).map((_, i) => (
                    <React.Fragment key={i}>{children}</React.Fragment>
                ))}
            </motion.div>
        </div>
    );
};

export default VelocityRow;
